import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Schemas } from '../api/client.js';
import { track } from '../api/events.js';
import { ServiceRequestForm } from './ServiceRequestForm.js';
import { formatPrice } from './site.js';
import { Button } from './ui.js';

/** "Hilfe anfordern (ab 79 €)": opens the service request in a modal dialog (US-05). */
export function HelpButton({
  siteId,
  service,
  findingId,
  ruleCode,
}: {
  siteId: string;
  service: Schemas['ServiceRef'];
  findingId?: string;
  ruleCode?: string;
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const price = formatPrice(service.priceFromCents, i18n.language);

  useEffect(() => {
    const el = dialog.current;
    if (!el) return;
    if (open && !el.open) {
      // jsdom and old browsers lack showModal; the dialog is then shown inline.
      if (typeof el.showModal === 'function') el.showModal();
      else el.setAttribute('open', '');
    }
    if (!open && el.open) {
      if (typeof el.close === 'function') el.close();
      else el.removeAttribute('open');
    }
  }, [open]);

  return (
    <>
      <Button
        onClick={() => {
          track('service_request_opened', {
            serviceCode: service.code,
            ...(ruleCode ? { ruleCode } : {}),
          });
          setOpen(true);
        }}
      >
        {service.from ? t('help.ctaFrom', { price }) : t('help.cta', { price })}
      </Button>
      <dialog
        ref={dialog}
        aria-labelledby={titleId}
        onClose={() => setOpen(false)}
        className="m-auto w-[calc(100%-2rem)] max-w-lg rounded-lg p-0 shadow-xl backdrop:bg-slate-900/50"
      >
        {open ? (
          <div className="flex max-h-[85vh] flex-col overflow-y-auto p-4 sm:p-6">
            <div className="mb-4 flex items-start justify-between gap-3">
              <h2 id={titleId} className="text-xl font-bold">
                {t('help.title')}
              </h2>
              <Button
                variant="secondary"
                onClick={() => setOpen(false)}
                aria-label={t('request.close')}
              >
                ✕
              </Button>
            </div>
            <ServiceRequestForm
              target={{ siteId, serviceCode: service.code, ...(findingId ? { findingId } : {}) }}
              onDone={() => setOpen(false)}
            />
          </div>
        ) : null}
      </dialog>
    </>
  );
}
